import React, { useState, useEffect, useRef } from 'react';
import { Alert, Card, Col, Container, Nav, Row, Spinner, Tab } from 'react-bootstrap';
import DashboardTable from '../Components/DashboardClienteTable';
import DashboardTipoAcessSetorTable from '../Components/DashboardClienteTipoAcessorioSetorTable';
import DashboardAccessoryBarChart from '../Components/DashboardClienteAccessoryBarChart';
import DashboardAccessoryPieChart from '../Components/DashboardClienteAccessoryPieChart';
import DashboardComparisonPieCharts from '../Components/DashboardComparisonPieCharts';
import DashboardCnpjYearComparisonChart from '../Components/DashboardCnpjYearComparisonChart';
import axios from '../Config/axiosConfig';
import "./DashboardCliente.css";
import DashboardExtraKpis from '../Shared/Kpis/DashboardExtraKpis';
import { buildDashboardKpis, buildYearlyConclusionComparison } from '../Shared/Utils/dashboardKpis';

//Compartilhados
import Loading from '../Shared/Loading/Loading';
import './../Shared/sharedStyles.css';

const DashboardCliente = () => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState([]);
  const [totals, setTotals] = useState({ aprovado: 0, aprovadoRecomendacoes: 0, reprovado: 0, total: 0 });
  const [totalsByAcessorio, setTotalsByAcessorio] = useState([]);
  const [accessoryTotals, setAccessoryTotals] = useState({});
  const [activeTab, setActiveTab] = useState('inspecoes');
  const [cnpjKpis, setCnpjKpis] = useState(null);
  const [cnpjKpisLoading, setCnpjKpisLoading] = useState(false);
  const [cnpjKpisError, setCnpjKpisError] = useState('');

  const baseApiUrl = process.env.REACT_APP_API_URL;
  const baseApi = `${baseApiUrl}/api/DashboardCliente`;

  const initialized = useRef(false);

  useEffect(() => {
    if (!initialized.current) {
      initialized.current = true;
      fetchInitialData();
    }
  }, []);

  const fetchInitialData = async () => {
    setLoading(true);
    try {
      const responseList = await Promise.allSettled([axios.get(`${baseApi}/get-sub-setor-statistics`)]);

      const allData = responseList[0].status === 'fulfilled' ? responseList[0].value.data : [];

      const filteredData = allData.filter(item => item.acessorio !== null);

      const totalData = allData.find(item => item.acessorio === null);

      setData(filteredData);

      if (totalData) {
        setTotals({
          aprovado: totalData.aprovado,
          aprovadoRecomendacoes: totalData.aprovadoRecomendacoes,
          reprovado: totalData.reprovado,
          total: totalData.totalPorAcessorio
        });

        const totalsByAcessorio = filteredData.reduce((acc, item) => {
          if (!acc[item.acessorio]) {
            acc[item.acessorio] = {
              acessorio: item.acessorio,
              total: 0,
              porcentagem: 0
            };
          }
          acc[item.acessorio].total += item.totalPorAcessorio;
          return acc;
        }, {});

        for (let acessorio in totalsByAcessorio) {
          totalsByAcessorio[acessorio].porcentagem = ((totalsByAcessorio[acessorio].total / totalData.totalPorAcessorio) * 100).toFixed(2);
        }

        setTotalsByAcessorio(Object.values(totalsByAcessorio));
      }

      const accessoryTotals = groupByAcessorio(filteredData);
      setAccessoryTotals(accessoryTotals);

    } catch (error) {
      console.log('Erro ao buscar dados:', error);
    }
    setLoading(false);
  };

  const fetchCnpjKpis = async () => {
    setCnpjKpisLoading(true);
    setCnpjKpisError('');

    try {
      const response = await axios.get(`${baseApi}/get-kpi-dataset-by-cnpj`);
      const dataset = response.data || {};
      const laudos = dataset.laudos || [];
      const kpis = buildDashboardKpis({
        laudos,
        ordensServico: dataset.ordensServico || [],
        includeClientRanking: false,
        includeOrderServiceKpis: false,
        includeValidityKpis: false,
        includeInspectorRanking: false
      });
      setCnpjKpis({ ...kpis, yearlyComparison: buildYearlyConclusionComparison(laudos) });
    } catch (error) {
      console.log('Erro ao buscar KPIs por CNPJ:', error);
      setCnpjKpisError('Não foi possível carregar os KPIs consolidados por CNPJ.');
    } finally {
      setCnpjKpisLoading(false);
    }
  };

  const handleTabSelect = (key) => {
    setActiveTab(key);

    if (key === 'kpis-cnpj' && !cnpjKpis && !cnpjKpisLoading) {
      fetchCnpjKpis();
    }
  };

  const groupBySubSetor = (data) => {
    return data.reduce((acc, item) => {
      if (!acc[item.subSetor]) {
        acc[item.subSetor] = {
          aprovado: 0,
          aprovadoRecomendacoes: 0,
          reprovado: 0
        };
      }
      acc[item.subSetor].aprovado += item.aprovado;
      acc[item.subSetor].aprovadoRecomendacoes += item.aprovadoRecomendacoes;
      acc[item.subSetor].reprovado += item.reprovado;
      return acc;
    }, {});
  };

  const groupByAcessorio = (data) => {
    return data.reduce((acc, item) => {
      if (!acc[item.acessorio]) {
        acc[item.acessorio] = {
          aprovado: 0,
          aprovadoRecomendacoes: 0,
          reprovado: 0
        };
      }
      acc[item.acessorio].aprovado += item.aprovado;
      acc[item.acessorio].aprovadoRecomendacoes += item.aprovadoRecomendacoes;
      acc[item.acessorio].reprovado += item.reprovado;
      return acc;
    }, {});
  };

  const getUniqueAcessorios = (data) => {
    const allAcessorios = data.map(item => item.acessorio);
    return [...new Set(allAcessorios)].sort();  // Sort accessories alphabetically
  };

  const groupedData = groupBySubSetor(data);
  const uniqueAcessorios = getUniqueAcessorios(data);

  if (loading) {
    return <Loading />;
  }

  return (
    <Container fluid>
      <Tab.Container activeKey={activeTab} onSelect={handleTabSelect}>
        <Nav variant="tabs" className="mb-3" aria-label="Dashboard do cliente">
          <Nav.Item>
            <Nav.Link eventKey="inspecoes">Inspeções</Nav.Link>
          </Nav.Item>
          <Nav.Item>
            <Nav.Link eventKey="kpis-cnpj">KPIs por CNPJ</Nav.Link>
          </Nav.Item>
        </Nav>

        <Tab.Content>
          <Tab.Pane eventKey="inspecoes">
            <DashboardTable groupedData={groupedData} uniqueAcessorios={uniqueAcessorios} totals={totals} data={data} accessoryTotals={accessoryTotals} />
            <DashboardTipoAcessSetorTable totalsByAcessorio={totalsByAcessorio} />
            <DashboardAccessoryBarChart totalsByAcessorio={totalsByAcessorio} />

            <Card>
              <Card.Body>
                <Row>
                  {Object.keys(accessoryTotals).map((acessorio, index) => (
                    <Col key={index} md={4}>
                      <DashboardAccessoryPieChart acessorio={acessorio} data={accessoryTotals[acessorio]} />
                    </Col>
                  ))}
                </Row>
              </Card.Body>
            </Card>

            <DashboardComparisonPieCharts totals={totals} />
            <Card className="kpi-section-card mt-4">
              <Card.Body>
                <Card.Title className="kpi-section-title" as="h4">KPIs de risco e vencimento</Card.Title>
                <p className="kpi-section-subtitle">Indicadores complementares para apoiar a gestão do cliente.</p>
              </Card.Body>
            </Card>
          </Tab.Pane>

          <Tab.Pane eventKey="kpis-cnpj">
            <Card className="kpi-section-card">
              <Card.Body>
                <Card.Title className="kpi-section-title" as="h4">KPIs consolidados por CNPJ</Card.Title>
                <p className="kpi-section-subtitle">Esta visão reúne todos os registros vinculados ao mesmo CNPJ, inclusive os cadastros de outros anos.</p>
              </Card.Body>
            </Card>

            {cnpjKpisLoading && (
              <div className="text-center py-5" role="status">
                <Spinner animation="border" size="sm" className="me-2" />
                Carregando KPIs por CNPJ…
              </div>
            )}
            {cnpjKpisError && <Alert variant="danger" className="mt-3">{cnpjKpisError}</Alert>}
            {cnpjKpis && !cnpjKpisLoading && (
              <DashboardExtraKpis
                title="Indicadores do CNPJ"
                subtitle="Indicadores complementares para apoiar a gestão do cliente."
                cards={cnpjKpis.cards}
                rankings={cnpjKpis.rankings}
              />
            )}
            {cnpjKpis && !cnpjKpisLoading && <DashboardCnpjYearComparisonChart comparisons={cnpjKpis.yearlyComparison} />}
          </Tab.Pane>
        </Tab.Content>
      </Tab.Container>

    </Container>
  );
};

export default DashboardCliente;
